/**
 * Reference: https://github.com/sergi/virtual-list/blob/master/vlist.js
 */

/**
 * virtual-scroller
 * @constructor
 * @param {number} width - css value for width property
 * @param {number} height - css value for height property
 * @param {number} itemHeight - height of one row
 * @param {number} maxRowsToShow - number of rows to be display in virtual scroller
 * @param {number} topOffset - topOffset for row translate
 */

/**
 * @init
 * @param {number} totalRows - css value for width property
 * @param {array} items - row items array
 * @param {function} generatorFn - function for render custom row
 */

"use strict";

export default class VirtualScroll {
  constructor({
    width,
    height,
    itemHeight,
    maxRowsToShow = 200,
    topOffset = 0,
  }) {
    this.width = (width && width + "px") || "100%";
    this.height = (height && height + "px") || "100%";
    this.itemHeight = itemHeight;
    this.maxRowsToShow = maxRowsToShow;
    this.topOffset = topOffset;
  }

  init = ({ totalRows, items, generatorFn, onRender = () => {} }) => {
    this.totalRows = totalRows || (items && items.length);
    this.items = items;
    this.generatorFn = generatorFn;

    const itemHeight = this.itemHeight;

    const scroller = (this.scroller = this.createScroller(
      itemHeight * this.totalRows
    ));

    this.container = this.createContainer(this.width, this.height);
    this.container.appendChild(scroller);

    this.h = this.container.clientHeight || 500;

    let screenItemsLen = Math.ceil(this.h / itemHeight);
    this.cachedItemsLen =
      screenItemsLen * 3 < this.maxRowsToShow
        ? this.maxRowsToShow
        : screenItemsLen * 3;
    if (this.totalRows > 0) this.renderChunk(this.container, 0);
    const self = this;
    onRender(self.container);

    let lastRepaintY;
    let lastScrolled = 0;

    // As soon as scrolling has stopped, this interval asynchronously removes all
    // the nodes that are not used anymore
    this.rmNodeInterval = setInterval(function () {
      if (Date.now() - lastScrolled > 100) {
        const badNodes = document.querySelectorAll('[data-rm="1"]');
        for (let i = 0, l = badNodes.length; i < l; i++) {
          if (self.container.contains(badNodes[i]))
            self.container.removeChild(badNodes[i]);
        }
      }
    }, 300);

    function onScroll(e) {
      const scrollTop = e.target.scrollTop; // Triggers reflow
      self.h = self.container.clientHeight || 500;
      screenItemsLen = Math.ceil(self.h / itemHeight);
      const maxBuffer = screenItemsLen * itemHeight;

      if (!lastRepaintY || Math.abs(scrollTop - lastRepaintY) > maxBuffer) {
        const first = parseInt(scrollTop / itemHeight) - screenItemsLen;
        self.renderChunk(self.container, first < 0 ? 0 : first);
        lastRepaintY = scrollTop;
        onRender(self.container);
      }

      lastScrolled = Date.now();
      e.preventDefault && e.preventDefault();
    }

    this.container.addEventListener("scroll", onScroll);
  };

  resize = (vHeight = window.innerHeight, canCheckOffsetParent) => {
    if (this.container) {
      const height = utils.getFitHeight(
        this.container,
        vHeight,
        50,
        canCheckOffsetParent
      );
      if (height) {
        this.container.style.height = height;
        // Recalculate height-dependent values after resize
        this.h = this.container.clientHeight || 500;
        const screenItemsLen = Math.ceil(this.h / this.itemHeight);
        this.cachedItemsLen =
          screenItemsLen * 3 < this.maxRowsToShow
            ? this.maxRowsToShow
            : screenItemsLen * 3;
        // Force a re-render to ensure proper display after resize
        if (this.totalRows > 0) {
          const currentScrollTop = this.container.scrollTop;
          const first =
            parseInt(currentScrollTop / this.itemHeight) - screenItemsLen;
          this.renderChunk(this.container, first < 0 ? 0 : first);
        }
      }
    }
  };

  createRow = (i) => {
    let item;
    if (this.generatorFn) item = this.generatorFn(i);
    else if (this.items) {
      if (typeof this.items[i] === "string") {
        const itemText = document.createTextNode(this.items[i]);
        item = document.createElement("div");
        item.style.height = this.itemHeight + "px";
        item.appendChild(itemText);
      } else {
        item = this.items[i];
      }
    }

    item.style.position = "absolute";
    item.style.transform =
      "translate(0px," + (i * this.itemHeight + this.topOffset) + "px)";
    return item;
  };

  isItemInView = (index) => {
    const scrollTop = this.container.scrollTop;
    const itemPosition = index * this.itemHeight;

    return scrollTop < itemPosition;
  };

  scrollToIndex = (index = 0, behavior = "smooth") => {
    this.container?.scrollTo({
      top: index * this.itemHeight + this.topOffset,
      behavior: behavior,
    });
  };

  renderChunk = (node, from) => {
    let finalItem = from + this.cachedItemsLen;
    if (finalItem > this.totalRows) finalItem = this.totalRows;

    // Append all the new rows in a document fragment that we will later append to
    // the parent node
    const fragment = document.createDocumentFragment();
    for (let i = from; i < finalItem; i++) {
      fragment.appendChild(this.createRow(i));
    }

    // Hide and mark obsolete nodes for deletion.
    for (let j = 1, l = node.childNodes.length; j < l; j++) {
      node.childNodes[j].style.display = "none";
      node.childNodes[j].setAttribute("data-rm", "1");
    }
    node.appendChild(fragment);
  };

  createContainer = (w, h) => {
    const container = document.createElement("div");
    container.style.width = w;
    container.style.height = h;
    container.style.overflow = "hidden auto";
    container.style.position = "relative";
    container.style.padding = 0;
    return container;
  };

  createScroller = (h) => {
    const scroller = document.createElement("div");
    scroller.style.opacity = 0;
    scroller.style.position = "absolute";
    scroller.style.top = 0;
    scroller.style.left = 0;
    scroller.style.width = "1px";
    scroller.style.height = h + "px";
    return scroller;
  };
}
